import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import Category from '../../../models/Category';
import InfoPage from '../../../models/InfoPage';
import FullProduct from '../../../models/product/FullProduct';
import Section from '../../../models/Section';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import LanguageStore from '../../../store/LanguageStore';
import SEOLink from '../SEOLink';
import SectionStore from "../../../store/SectionStore";
import ProductClass from "../../../types/ProductClass";

const AlternateLink = ({
  languageStore,
  sectionStore,
  routeService,
  model,
  isCategoryPage,
  isProductPage,
  isSectionPage,
  isInfoPage,
  path,
}) => {
  const languages = languageStore.languages;
  if (languages.length <= 1) {
    return null;
  }

  const renderAlternateLinks = () => {
    return languages.map((language) => {
      return isTranslated(language.code) ? (
        <SEOLink
          key={language.code}
          rel="alternate"
          path={getPath(language.code)}
          hreflang={language.code}
        />
      ) : null;
    });
  };

  const isTranslated = (languageCode) => {
    // Content pages should not be shown when untranslated.
    if (isInfoPage && model) {
      return model.hasLocalizedPath(languageCode) || languageStore.activeLocale === languageCode;
    }
    return true;
  }

  const getPath = (languageCode) => {
    if (isCategoryPage && model) {
      return routeService.getCanonicalCategoryPath(
        model,
        model.getLocalizedPath(languageCode),
        languageCode
      );
    }

    if (isProductPage && model) {
      return routeService.getCanonicalProductPath(
        model,
        model.getLocalizedPath(
          languageCode,
          model.class === ProductClass.MULTI_CHILD ? model.id : null
        ),
        languageCode
      );
    }

    if (isSectionPage && model) {
      return routeService.getPath(
        model.getLocalizedPath(languageCode),
        null,
        languageCode
      );
    }

    if (isInfoPage && model) {
      return routeService.getPath(
        model.getLocalizedPath(languageCode),
        null,
        languageCode
      );
    }
    return routeService.getPath(
      path,
      isSectionPage ? sectionStore.activeSection : null,
      languageCode
    );
  };

  return renderAlternateLinks();
};

AlternateLink.propTypes = {
  languageStore: modelOf(LanguageStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  model: PropTypes.oneOfType([
    modelOf(Category),
    modelOf(FullProduct),
    modelOf(Section),
    modelOf(InfoPage),
    PropTypes.object,
  ]),
  isCategoryPage: PropTypes.bool,
  isProductPage: PropTypes.bool,
  isSectionPage: PropTypes.bool,
  isInfoPage: PropTypes.bool,
  path: PropTypes.string,
};

export default inject(
  'languageStore',
  'sectionStore',
  'routeService'
)(observer(AlternateLink));
